/**
 * Intake Table Component
 * 
 * Displays intake data in a table format with actions
 */

import React from 'react';
import { useClinic } from '@/contexts/ClinicContext';
import { 
  CheckCircle2, 
  Clock, 
  XCircle,
  RefreshCw,
  Mail,
  Eye,
  Trash2,
  MoreHorizontal,
  FileText,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  PendingIntake,
  getTherapyCategoryLabel,
  getIntakeTypeLabel,
} from '@/services/intakeService';

interface IntakeTableProps {
  intakes: PendingIntake[];
  onResend: (intakeId: string) => void;
  onCancel: (intakeId: string) => void;
  onView?: (intakeId: string) => void;
  onDownload?: (responseId: string) => void;
}

const StatusBadge: React.FC<{ status: string; isReviewed?: boolean }> = ({ status, isReviewed }) => {
  const config: Record<string, { label: string; className: string; icon: React.ElementType }> = {
    pending: {
      label: 'Pending',
      className: 'bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-2 py-0.5',
      icon: Clock,
    },
    started: {
      label: 'In Progress',
      className: 'bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-2 py-0.5',
      icon: RefreshCw,
    },
    completed: {
      label: 'Completed',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-2 py-0.5',
      icon: CheckCircle2,
    },
    reviewed: {
      label: 'Reviewed',
      className: 'bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] px-2 py-0.5',
      icon: CheckCircle2,
    },
    expired: {
      label: 'Expired',
      className: 'bg-red-50 text-red-700 border-red-200 text-[10px] px-2 py-0.5',
      icon: XCircle,
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-slate-50 text-slate-700 border-slate-200 text-[10px] px-2 py-0.5',
      icon: XCircle,
    },
  };

  // Show "reviewed" status if completed and reviewed
  const displayStatus = (status === 'completed' && isReviewed) ? 'reviewed' : status;
  const { label, className, icon: Icon } = config[displayStatus] || config.pending;

  return (
    <Badge variant="outline" className={className}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Helper to handle snake_case and camelCase field names
const getField = (intake: PendingIntake, ...keys: (keyof PendingIntake)[]): any => {
  for (const key of keys) {
    if (intake[key] !== undefined) return intake[key];
  }
  return undefined;
};

const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTimeAgo = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

export const IntakeTable: React.FC<IntakeTableProps> = ({
  intakes,
  onResend,
  onCancel,
  onView,
  onDownload,
}) => {
  const { clinic } = useClinic();
  const formLabel = clinic?.clinic_archetype === 'guided' ? 'enrollment' : 'intake';
  const formLabelPlural = clinic?.clinic_archetype === 'guided' ? 'enrollments' : 'intakes';

  if (intakes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center border border-slate-200 rounded-md bg-white">
        <FileText className="w-10 h-10 text-slate-300 mb-3" />
        <h3 className="text-sm font-medium text-slate-700 mb-1">No {formLabelPlural} found</h3>
        <p className="text-xs text-slate-500 max-w-md">
          There are no {formLabelPlural} matching your current filters. Try adjusting the filters or create a new {formLabel} request.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
            <tr className="border-b border-slate-200">
              <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                Patient
              </th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                Category
              </th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                Status
              </th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                Sent
              </th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                Expires
              </th>
              <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {intakes.map((intake) => {
              const patientName = getField(intake, 'patient_name', 'patientName') || 'Unknown Patient';
              const patientEmail = getField(intake, 'patient_email', 'patientEmail') || '';
              const therapyCategory = getField(intake, 'therapy_category', 'therapyCategory');
              const createdAt = getField(intake, 'created_at', 'createdAt');
              const expiresAt = getField(intake, 'expires_at', 'expiresAt');
              const completedAt = getField(intake, 'completed_at', 'completedAt');
              const updatedAt = getField(intake, 'updated_at');
              const responseId = getField(intake, 'questionnaire_response_id', 'questionnaireResponseId', 'response_id', 'responseId');
              const isReviewed = getField(intake, 'is_reviewed', 'isReviewed') || false;
              const reviewedAt = getField(intake, 'reviewed_at', 'reviewedAt');
              
              return (
              <tr 
                key={intake.id} 
                className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0"
              >
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900 capitalize">
                      {patientName}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {patientEmail}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge 
                    variant="outline" 
                    className="bg-brand-teal/10 text-brand-teal border-brand-teal/20 text-[10px] px-2 py-0.5"
                  >
                    {getTherapyCategoryLabel(therapyCategory)}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={intake.status} isReviewed={isReviewed} />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-xs text-slate-900">
                      {formatDate(createdAt)}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {createdAt ? getTimeAgo(createdAt) : 'N/A'}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {intake.status === 'completed' && isReviewed ? (
                    <p className="text-xs text-indigo-600 font-medium">
                      Reviewed {formatDate(reviewedAt || completedAt || updatedAt)}
                    </p>
                  ) : intake.status === 'completed' ? (
                    <p className="text-xs text-emerald-600 font-medium">
                      Completed {formatDate(completedAt || updatedAt)}
                    </p>
                  ) : intake.status === 'expired' ? (
                    <p className="text-xs text-red-600 font-medium">Expired</p>
                  ) : (
                    <div>
                      <p className="text-xs text-slate-900">
                        {formatDate(expiresAt)}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {expiresAt && new Date(expiresAt) < new Date() ? 'Expired' : formatTime(expiresAt)}
                      </p>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-7 w-7 p-0"
                      >
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {intake.status === 'completed' && (
                        <>
                          <DropdownMenuItem 
                            className="text-xs cursor-pointer"
                            onClick={() => onView?.(responseId || intake.id)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-2" />
                            View Response
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-xs cursor-pointer"
                            onClick={() => onDownload?.(responseId || intake.id)}
                          >
                            <Download className="w-3.5 h-3.5 mr-2" />
                            Download PDF
                          </DropdownMenuItem>
                        </>
                      )}
                      {(intake.status === 'pending' || intake.status === 'started') && (
                        <>
                          <DropdownMenuItem
                            className="text-xs cursor-pointer"
                            onClick={() => onResend(intake.id)}
                          >
                            <Mail className="w-3.5 h-3.5 mr-2" />
                            Resend Email
                          </DropdownMenuItem>
                          {!(intake as any).guided_order_id && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-xs text-red-600 cursor-pointer"
                                onClick={() => onCancel(intake.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-2" />
                                Cancel Intake
                              </DropdownMenuItem>
                            </>
                          )}
                        </>
                      )}
                      {(intake.status === 'expired' || intake.status === 'cancelled') && (
                        <DropdownMenuItem 
                          className="text-xs cursor-pointer"
                          onClick={() => onResend(intake.id)}
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-2" />
                          Resend Request
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IntakeTable;
